import { registerProduct } from "@/common/services/product.api";
import { TYPE_MEASUREDUNIT } from "@/types/product.types";
import React, { useMemo, useState } from "react";

const MEASURED_UNITS = ["CJ", "KIT", "UNID"] as const;

export type CreateProductPayload = {
    code: string;
    name: string;
    brand?: string;
    category: string;
    maximumApplication: number;
    durability: number;
    preventive: boolean;
    corrective: boolean;
    misuse: boolean;
    qtdFrac: number;
    measuredunit: TYPE_MEASUREDUNIT,
    referencesSet?: string[];
    ncm: string;
    cst: string;
};

type Props = {
    onSubmit: () => void
}

export default function FormRegisterProduct(props: Props) {
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [form, setForm] = useState<CreateProductPayload>({
        code: "",
        name: "",
        brand: "",
        category: "",
        maximumApplication: 0,
        durability: 0,
        preventive: false,
        corrective: false,
        misuse: false,
        qtdFrac: 0,
        measuredunit: TYPE_MEASUREDUNIT.UNID,
        referencesSet: [],
        ncm: "",
        cst: ""
    });

    const isValid = useMemo(() => {
        return (
            form.code.trim().length > 0 &&
            form.name.trim().length > 0 &&
            form.category.trim().length > 0 &&
            !Number.isNaN(form.maximumApplication) &&
            !Number.isNaN(form.durability) &&
            !Number.isNaN(form.qtdFrac)
        );
    }, [form]);

    function handleChange<K extends keyof CreateProductPayload>(
        key: K,
        value: CreateProductPayload[K]
    ) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setLoading(true);
        setSuccess(null);
        setError(null);

        try {
            const dataToSend: CreateProductPayload = {
                ...form,
                maximumApplication: Number(form.maximumApplication),
                durability: Number(form.durability),
                qtdFrac: Number(form.qtdFrac),
            };

            const resp = await registerProduct(dataToSend);
            if (!resp.ok) {
                throw new Error(resp.message || "Falha ao cadastrar produto");
            }

            setSuccess("Produto cadastrado com sucesso.");
            props.onSubmit();
            setForm((prev) => ({
                ...prev,
                code: "",
                name: "",
                brand: "",
                category: "",
                referencesSet: [],
            }));
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            setError(err?.message || "Falha ao cadastrar produto");
        } finally {
            setLoading(false);
        }
    }

    const inputCls = "px-3 py-2 border border-gray-300 rounded bg-white outline-none focus:ring-2 focus:ring-gray-900/20 focus:border-gray-400";

    return (
        <div>
            <h1 className="text-2xl font-semibold mb-4">Preencha as inf. do produto abaixo</h1>

            <div>
                <div>
                    <form
                        onSubmit={handleSubmit}
                        className="flex flex-col gap-3"
                    >
                        <div className="flex flex-col gap-4 md:flex-row">
                            <div className="space-y-1 flex flex-col w-full">
                                <label htmlFor="code">Código *</label>
                                <input
                                    id="code"
                                    placeholder="Digite o código"
                                    value={form.code}
                                    onChange={(e) => handleChange("code", e.target.value)}
                                    required
                                    className={inputCls}
                                />
                            </div>
                            <div className="space-y-1 flex flex-col w-full">
                                <label htmlFor="category">Categoria *</label>
                                <input
                                    id="category"
                                    placeholder="Freios"
                                    value={form.category}
                                    onChange={(e) => handleChange("category", e.target.value)}
                                    required
                                    className={inputCls}
                                />
                            </div>
                            <div className="space-y-1 flex flex-col w-full">
                                <label htmlFor="name">Nome *</label>
                                <input
                                    id="name"
                                    placeholder="Pastilha de Freio"
                                    value={form.name}
                                    onChange={(e) => handleChange("name", e.target.value)}
                                    required
                                    className={inputCls}
                                />
                            </div>
                        </div>

                        <div className="space-y-1 flex flex-col">
                            <label htmlFor="brand">Marca *</label>
                            <input
                                id="brand"
                                placeholder="Brembo"
                                value={form.brand}
                                onChange={(e) => handleChange("brand", e.target.value)}
                                className={inputCls}
                            />
                        </div>

                        <div className="space-y-1 flex flex-col">
                            <label htmlFor="maximumApplication">Aplicação Máxima *</label>
                            <input
                                id="maximumApplication"
                                type="number"
                                min={0}
                                value={form.maximumApplication === 0 || form.maximumApplication === undefined ? "" : form.maximumApplication}
                                onChange={(e) =>
                                    handleChange("maximumApplication", Number(e.target.value))
                                }
                                required
                                className={inputCls}
                            />
                        </div>

                        {/* Inputs secundários quebram em coluna no mobile */}
                        <div className="flex flex-col gap-4 md:flex-row">
                            <div className="space-y-1 flex flex-col w-full">
                                <label htmlFor="durability">Durabilidade (meses) *</label>
                                <input
                                    id="durability"
                                    type="number"
                                    min={0}
                                    value={form.durability === 0 || form.durability === undefined ? "" : form.durability}
                                    onChange={(e) => handleChange("durability", Number(e.target.value))}
                                    required
                                    className={inputCls}
                                />
                            </div>
                            <div className="space-y-1 flex flex-col w-full">
                                <label htmlFor="qtdFrac">Qtd. Fracionada</label>
                                <input
                                    id="qtdFrac"
                                    type="number"
                                    min={0}
                                    value={form.qtdFrac === 0 || form.qtdFrac === undefined ? "" : form.qtdFrac}
                                    onChange={(e) => handleChange("qtdFrac", Number(e.target.value))}
                                    className={inputCls}
                                />
                            </div>
                            <div className="space-y-1 flex flex-col w-full">
                                <label htmlFor="measuredunit">Unidade de Medida</label>
                                <select
                                    id="measuredunit"
                                    value={form.measuredunit}
                                    onChange={(e) => handleChange("measuredunit", e.target.value as TYPE_MEASUREDUNIT)}
                                    required
                                    className={inputCls}
                                >
                                    {MEASURED_UNITS.map((u) => (
                                        <option key={u} value={u}>
                                            {u}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Checkboxes quebram em coluna no mobile */}
                        <div className="flex flex-col gap-4 md:flex-row">
                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-xl w-full">
                                <div>
                                    <label htmlFor="preventive" className="cursor-pointer">
                                        Preventivo
                                    </label>
                                    <div className="text-xs text-gray-500">
                                        Indicador para manutenções preventivas
                                    </div>
                                </div>
                                <input
                                    id="preventive"
                                    type="checkbox"
                                    checked={form.preventive}
                                    onChange={(e) => handleChange("preventive", e.target.checked)}
                                />
                            </div>
                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-xl w-full">
                                <div>
                                    <label htmlFor="corrective" className="cursor-pointer">
                                        Corretivo
                                    </label>
                                    <div className="text-xs text-gray-500">
                                        Indicador para manutenções corretivas
                                    </div>
                                </div>
                                <input
                                    id="corrective"
                                    type="checkbox"
                                    checked={form.corrective}
                                    onChange={(e) => handleChange("corrective", e.target.checked)}
                                />
                            </div>
                            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-xl w-full">
                                <div>
                                    <label htmlFor="misuse" className="cursor-pointer">
                                        Mau uso
                                    </label>
                                    <div className="text-xs text-gray-500">Marcar quando aplicável</div>
                                </div>
                                <input
                                    id="misuse"
                                    type="checkbox"
                                    checked={form.misuse}
                                    onChange={(e) => handleChange("misuse", e.target.checked)}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-4 md:flex-row">
                            <div className="space-y-1 flex flex-col w-full">
                                <label htmlFor="durability">NCM *</label>
                                <input
                                    id="ncm"
                                    type="text"
                                    onChange={(e) => handleChange("ncm", e.target.value)}
                                    required
                                    className={inputCls}
                                />
                            </div>
                            <div className="space-y-1 flex flex-col w-full">
                                <label htmlFor="durability">CST *</label>
                                <input
                                    id="cst"
                                    type="text"
                                    onChange={(e) => handleChange("cst", e.target.value)}
                                    required
                                    className={inputCls}
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                type="submit"
                                disabled={!isValid || loading}
                                className="px-4 py-2 rounded-lg bg-locavibe text-white"
                            >
                                {loading ? "Salvando..." : "Cadastrar Produto"}
                            </button>
                        </div>
                    </form>

                    {success && <div className="mt-4 text-green-600">{success}</div>}
                    {error && <div className="mt-4 text-red-600">{error}</div>}
                </div>
            </div>
        </div>
    );
}